import api from 'api';
import { useCallback, useContext, useMemo } from 'react';
import { EnquiryFormValues } from '../components/EnquirySlider/utils';
import { useNavigate } from 'react-router-dom';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { Listing, STATUSES_TO_ENQUIRE } from 'models/Listing';
import { useTranslation } from 'react-i18next';
import { PROFILE_ROUTE } from 'data/global';
import { getBuyingPortfolio, getIsListingOwner } from 'utils';
import EnquiryContext from 'providers/Enquiry/Enquiry.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  ENQUIRY_ORIGIN,
  EnquiryCreationInitiatedEventProps,
} from 'models/MixpanelEvents';

export function useOnEnquire(listing: Partial<Listing>) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { currentUser } = useContext(CurrentUserContext);
  const { popServerError, popInfo } = useContext(PopNotificationsContext);
  const { closeModal } = useContext(EnquiryContext);

  const getMixpanelProps = useGetMixpanelProps();

  const {
    _id,
    enableTeaser,
    listingEnquiryId,
    userId,
    isCoOwner,
    isEnquired,
    stage,
    dealRoomId,
    dealRoom,
  } = listing || {};
  const { _id: currentUserId } = currentUser || {};

  const dealName = dealRoom?.name || '';

  const isOwner = useMemo(
    () => getIsListingOwner(currentUserId, userId, isCoOwner),
    [currentUserId, isCoOwner, userId],
  );

  return useCallback(
    async (formValues: EnquiryFormValues) => {
      if (!STATUSES_TO_ENQUIRE.includes(stage)) {
        popInfo({
          content: t('youCannotEnquireThisListing'),
        });
        return;
      }

      if (isOwner || isEnquired) {
        popInfo({
          content: t(isOwner ? 'yourOwnListing' : 'youHaveAlreadyEnquired'),
        });
        return;
      }

      const { questions, ...rest } = formValues;

      let finalValues: EnquiryFormValues = { ...rest };

      if (questions?.length) {
        const tmp = questions.filter(({ answer }) => !!answer);

        finalValues = { ...finalValues, questions: tmp };
      }

      try {
        mixpanelService.enquiryCreationInitiated(
          getMixpanelProps({
            dealName,
            dealId: dealRoomId,
            enquiryOrigin: ENQUIRY_ORIGIN.ORGANIC,
          }) as EnquiryCreationInitiatedEventProps,
        );

        const {
          data: { ndaUrl },
        } = await api.listings.registerInterest(_id, finalValues);

        if (enableTeaser) {
          if (ndaUrl) {
            window.location.href = ndaUrl;
          } else {
            navigate(PROFILE_ROUTE);
            closeModal();
          }
        } else {
          navigate(getBuyingPortfolio(listingEnquiryId));
          closeModal();
        }
      } catch (err) {
        popServerError(err);
      }
    },
    [
      stage,
      isOwner,
      isEnquired,
      _id,
      dealRoomId,
      enableTeaser,
      listingEnquiryId,
      dealName,
      getMixpanelProps,
      popInfo,
      t,
      navigate,
      popServerError,
      closeModal,
    ],
  );
}
